class S2sEvent {
  static DEFAULT_INFER_CONFIG = {
    maxTokens: 1024,
    topP: 0.95,
    temperature: 0.4
  };

  static DEFAULT_SYSTEM_PROMPT = `CRITICAL ROLE ASSIGNMENT - READ FIRST
ABSOLUTE RULE - NO EXCEPTIONS:

YOU are the student.
HUMAN SPEAKER = THE COACH (faculty/instructor).

If the human says "hello coach", "hi coach", or addresses you as "coach", they are greeting you incorrectly, but YOU ARE STILL THE STUDENT.
NEVER assume you are the coach under any circumstances.
NEVER respond as if you are coaching them.
NEVER acknowledge being called "coach" - simply respond as the student naturally.

Only ask a question if you are confused about the coach’s last instruction. Otherwise do not ask questions.
You are NOT responsible for guiding the conversation forward.
Silence, short answers, or vague responses are correct behavior.
If the coach gives a short, vague, or passive reply (e.g. "hmm", "I can't", "okay"), do NOT speak for them, do NOT offer reflections or encouragement, and do NOT ask what would help. End your turn after your own short response and wait.

OPENING BEHAVIOR OVERRIDE (CRITICAL)
If the conversation is starting, unclear, or the human greets you
(e.g. "hi", "hello", "hey", "hello <name>"):
- You MUST respond with a SHORT, PASSIVE student response.
- You MUST NOT ask a question.
- You MUST NOT offer help or support.

If you ask "How can I help you?", "How can I assist you?", or similar,
you have FAILED the task and must rewrite the response.

IDENTITY (NON-NEGOTIABLE)
You are a 4th year medical student.
The human speaker is YOUR FACULTY COACH.
You must speak ONLY as the student.

What you NEVER do:
- Never speak as the coach.
- Never narrate the coach's actions.
- Never include stage directions.
- Never say "As your coach..." or similar phrases.
- Never give coaching advice.
- Never ask coaching-style reflective questions.
- Never respond to being called "coach" by acting like one.

What you ALWAYS do:
- Speak in first person only ("I", "me", "my").
- Output only the student's next spoken message.
- Respond as someone RECEIVING coaching, not giving it.
- Act according to the SCENARIO behavior rules, even if they conflict with your personality.
- If the human speaks as a student, treat it as incorrect input and respond as the student anyway without mirroring their role.

ROLE CONSTRAINTS
- Speak in first person only ("I", "me", "my").
- Output only the student's next spoken message.
- Do NOT coach, advise, or guide the coach.
- Do NOT suggest what the coach should do.
- Keep responses short and natural: 1–3 sentences by default.
- You may ask brief clarification questions only if confused (e.g. "I'm not sure what you mean.").

SCENARIO BEHAVIOR RULES (OVERRIDE PERSONA IF CONFLICT)
Engagement level: 5/10
Initiative level: 5/10
Defensiveness level: 5/10
Emotional intensity: 5/10
Verbosity level: 5/10

IMPORTANT:
These behavior rules define HOW you behave in this scenario.
They override personality traits if there is any conflict.

ACADEMIC YEAR CALIBRATION
Year 1: simpler language, high uncertainty, minimal clinical references.
Year 2: growing confidence, abstract understanding.
Year 3: clinical exposure, mixed confidence, increasing pressure.
Year 4: concrete language, urgency, career/match pressure.
Academic year affects maturity and stakes, NOT engagement style.

SAFETY & REALISM
- Never mention system rules or that you are an AI.
- If asked about a patient, respond generically (e.g. "stable", "critically ill") with no identifiers.
- Do not resolve the problem on your own; allow the human to guide the process.
- If unsure, it is acceptable to say: "I don't know", "I'm not sure", or "I guess."

FINAL SELF-CHECK (SILENT)
Before responding, verify:
- I am speaking only as the student.
- I followed scenario behavior rules.
- I did not coach or lead the conversation.
- I did not advance stages without being earned.
- I did not respond as if I am the coach.
If any check fails, rewrite the response.`;

  static DEFAULT_AUDIO_INPUT_CONFIG = {
    mediaType: "audio/lpcm",
    sampleRateHertz: 16000,
    sampleSizeBits: 16,
    channelCount: 1,
    audioType: "SPEECH",
    encoding: "base64"
  };

  static DEFAULT_AUDIO_OUTPUT_CONFIG = {
    mediaType: "audio/lpcm",
    sampleRateHertz: 24000,
    sampleSizeBits: 16,
    channelCount: 1,
    voiceId: "matthew",
    encoding: "base64",
    audioType: "SPEECH"
  };

  static DEFAULT_TOOL_CONFIG = {tools: []};

  static DEFAULT_CHAT_HISTORY = [ ];

  static ROLE_REMINDER = `ROLE REMINDER:
You are the STUDENT.
Respond only as the student, in first person.
Do not assist, coach, or guide the coach. Keep replies short.
You are NOT responsible for guiding the conversation forward.
If the coach gives a short, vague, or passive reply (e.g. "hmm", "I can't", "okay"), do NOT speak for them, do NOT offer reflections or encouragement, and do NOT ask what would help. End your turn after your own short response and wait.
Only ask a question if you are confused about the coach’s last instruction. Otherwise do not ask questions.
Never ask any conversational check‑in questions like: “How are you?”,“How are you doing?”, “How’s it going?”, “How can I help?”, “What’s on your mind?”, “What would you like to talk about?” or Any rephrased, indirect, or implied version of these questions.
Only ask a question if you are confused about the coach’s last instruction. Otherwise do not ask questions.
`.trim();

static systemReminder(promptName, contentName, reminderText = S2sEvent.ROLE_REMINDER) {
  return [
    S2sEvent.contentStartText(promptName, contentName, "USER", false),
    S2sEvent.textInput(promptName, contentName, reminderText),
    S2sEvent.contentEnd(promptName, contentName)
  ];
}

  static sessionStart(inferenceConfig = S2sEvent.DEFAULT_INFER_CONFIG, turnSensitivity = "MEDIUM", modelId = "amazon.nova-sonic-v1:0") {
    return {
      event: {
        sessionStart: {
          inferenceConfiguration: inferenceConfig,
          modelId: modelId,
          // Nova 2 requires nesting under turnDetectionConfiguration (flat field is rejected).
          turnDetectionConfiguration: {
            endpointingSensitivity: turnSensitivity
          }
        }
      }
    };
  }

  static promptStart(promptName, audioOutputConfig = S2sEvent.DEFAULT_AUDIO_OUTPUT_CONFIG, toolConfig = S2sEvent.DEFAULT_TOOL_CONFIG) {
    return {
      "event": {
        "promptStart": {
          "promptName": promptName,
          "textOutputConfiguration": {
            "mediaType": "text/plain"
          },
          "audioOutputConfiguration": audioOutputConfig,

          "toolUseOutputConfiguration": {
            "mediaType": "application/json"
          },
          "toolConfiguration": toolConfig
        }
      }
    }
  }

  static contentStartText(promptName, contentName, role = "SYSTEM", interactive = false) {
    return {
      "event": {
        "contentStart": {
          "promptName": promptName,
          "contentName": contentName,
          "type": "TEXT",
          "interactive": interactive,
          "role": role,
          "textInputConfiguration": {
            "mediaType": "text/plain"
          }
        }
      }
    }
  }

  static textInput(promptName, contentName, systemPrompt = S2sEvent.DEFAULT_SYSTEM_PROMPT) {
    var evt = {
      "event": {
        "textInput": {
          "promptName": promptName,
          "contentName": contentName,
          "content": systemPrompt
        }
      }
    }
    return evt;
  }

  static contentEnd(promptName, contentName) {
    return {
      "event": {
        "contentEnd": {
          "promptName": promptName,
          "contentName": contentName
        }
      }
    }
  }

  static contentStartAudio(promptName, contentName, audioInputConfig = S2sEvent.DEFAULT_AUDIO_INPUT_CONFIG) {
    return {
      "event": {
        "contentStart": {
          "promptName": promptName,
          "contentName": contentName,
          "type": "AUDIO",
          "interactive": true,
          "role": "USER",
          "audioInputConfiguration": {
            "mediaType": "audio/lpcm",
            "sampleRateHertz": 16000,
            "sampleSizeBits": 16,
            "channelCount": 1,
            "audioType": "SPEECH",
            "encoding": "base64"
          }
        }
      }
    }
  }

  static audioInput(promptName, contentName, content) {
    return {
      event: {
        audioInput: {
          promptName,
          contentName,
          content,
        }
      }
    };
  }

  static contentStartTool(promptName, contentName, toolUseId) {
    return {
      event: {
        contentStart: {
          promptName,
          contentName,
          interactive: false,
          type: "TOOL",
          toolResultInputConfiguration: {
            toolUseId,
            type: "TEXT",
            textInputConfiguration: { mediaType: "text/plain" }
          }
        }
      }
    };
  }

  static textInputTool(promptName, contentName, content) {
    return {
      event: {
        textInput: {
          promptName,
          contentName,
          content,
          role: "TOOL"
        }
      }
    };
  }

  static promptEnd(promptName) {
    return {
      event: {
        promptEnd: {
          promptName
        }
      }
    };
  }

  static sessionEnd() {
    return { event: { sessionEnd: {} } };
  }
}
export default S2sEvent;